/**
 * The dashboard's Ship tab models (spec 2026-10-09 §2.2 "2 · Ship", §8 W4 card C2): Status, Loadout (read-only from
 * the Outfitting model's fit summary), Cargo (the manifest and its value at the best last-seen sell prices, with the
 * age of the oldest price used) and Fabrication (open fab orders, delivery progress, where to collect). Pure
 * Career-in, rows-out; the sections render them at the shell's refresh rate while open. Every word is `ship.json`'s.
 */
import { z } from 'zod'
import { contentsValue } from '../../../../career/cargo'
import { holdReadout } from '../../../../career/hullFit'
import { GOOD_NAMES, cargoUsedT } from '../../../../career/ops'
import { GOOD_BASE_PRICE, GOOD_IDS, type GoodId } from '../../../../career/parts'
import type { CargoComponent, Career, GoodOrWare } from '../../../../career/types'
import { resolveModule } from '../../../../data/m2'
import { WARE_IDS, type M2Data, type ModuleId, type SlotId, type StationId } from '../../../../data/m2Schemas'
import type { JettisonPhase } from '../../../../bridge/m2/encounters'
import { flawsOf } from '../../../../hulks/flaws'
import { wareIndexOf } from '../../../../strategic/wares'
import { formatCr, formatDuration, formatPct, formatTonnes } from '../../../format'
import { fabItemName, fitSummary } from '../../../models/outfitting'
import { UI_CONFIG } from '../../../config'
import shipJson from './ship.json'

const EPS = 1e-6
const text = z.string().min(1)
const section = z.object({ label: text, order: z.number() })
const dockedAction = z.object({ label: text, service: z.enum(['market', 'repair', 'outfitting', 'contracts', 'chart-buyer', 'parts-vendor', 'lease-office']), reason: text.optional() })

const shipTabSchema = z.object({
  comment: z.string().optional(),
  sections: z.object({ status: section, loadout: section, cargo: section, fabrication: section }),
  status: z.object({
    hull: text,
    shield: text,
    energy: text,
    torpedoes: text,
    noTelemetry: text,
    modules: text,
    conditionNew: text,
    insurance: text,
    insured: text,
    uninsured: text,
    lease: text,
    flaws: text,
    noFlaws: text,
  }),
  loadout: z.object({ slots: text, power: text, locker: text, lockerEmpty: text, salvaged: text, count: text }),
  cargo: z.object({
    capacity: text,
    empty: text,
    value: text,
    pricesSeen: text,
    pricesNever: text,
    basePrice: text,
    units: text,
    tonnes: text,
    flags: z.object({ contraband: text, sealed: text, story: text, contract: text }),
    notForSale: text,
    jettison: text,
    jettisonOff: text,
    jettisonHint: text,
  }),
  fabrication: z.object({ empty: text, progress: text, carry: text, ready: text, collectAt: text, setCourse: text }),
  dockedActions: z.object({
    status: z.array(dockedAction),
    loadout: z.array(dockedAction),
    cargo: z.array(dockedAction),
    fabrication: z.array(dockedAction),
  }),
})

export type ShipTabConfig = z.infer<typeof shipTabSchema>
export type ShipSectionKey = keyof ShipTabConfig['sections']
export type ShipDockedAction = ShipTabConfig['dockedActions'][ShipSectionKey][number]

/** `ship.json`, parsed once. */
export const SHIP_TAB: ShipTabConfig = shipTabSchema.parse(shipJson)

function fill(template: string, values: Readonly<Record<string, string>>): string {
  return template.replace(/\{(\w+)\}/g, (m, k: string) => values[k] ?? m)
}

// ---- Status --------------------------------------------------------------------------------------------------------

/** The live ship read off the sim (0..1 fractions); null when there is no live player ship. */
export interface ShipVitals {
  readonly hull: number
  readonly shield: number
  readonly energy: number
  readonly torpedoes: number
}

export interface StatusBar {
  readonly label: string
  readonly fraction: number
  readonly text: string
}

export interface ModuleConditionRow {
  readonly slot: SlotId
  readonly label: string
  readonly module: string
  readonly condition: string
}

export interface StatusModel {
  readonly bars: readonly StatusBar[]
  readonly torpedoes: string | null
  /** The no-telemetry line, or null. */
  readonly note: string | null
  readonly modules: readonly ModuleConditionRow[]
  readonly insurance: string
  readonly flaws: readonly string[]
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v
}

function insuranceText(career: Career): string {
  const t = SHIP_TAB.status
  const flown = career.flownShip === undefined ? undefined : career.hangar?.find((s) => s.fleetId === career.flownShip)
  if (flown !== undefined) return flown.insured ? t.insured : t.uninsured
  if (!career.lease.owned) return t.lease
  return career.lease.insured ? t.insured : t.uninsured
}

export function statusModel(data: M2Data, career: Career, vitals: ShipVitals | null): StatusModel {
  const t = SHIP_TAB.status
  const bar = (label: string, v: number): StatusBar => ({ label, fraction: clamp01(v), text: formatPct(clamp01(v)) })
  const salvaged = career.ship.salvaged?.fitted
  const modules = fitSummary(data, career).slots.map((s): ModuleConditionRow => {
    const sv = salvaged?.[s.slot]
    return { slot: s.slot, label: s.label, module: s.moduleName, condition: sv === undefined ? t.conditionNew : formatPct(sv.condition) }
  })
  const flown = career.flownShip === undefined ? undefined : career.hangar?.find((s) => s.fleetId === career.flownShip)
  return {
    bars: vitals === null ? [] : [bar(t.hull, vitals.hull), bar(t.shield, vitals.shield), bar(t.energy, vitals.energy)],
    torpedoes: vitals === null ? null : String(Math.max(0, Math.round(vitals.torpedoes))),
    note: vitals === null ? t.noTelemetry : null,
    modules,
    insurance: insuranceText(career),
    flaws: flawsOf(flown?.flaws ?? 0).map((f) => f.name),
  }
}

// ---- Loadout -------------------------------------------------------------------------------------------------------

export interface LoadoutSlotRow {
  readonly slot: SlotId
  readonly label: string
  readonly module: string
  readonly mk: string
  readonly power: string
  /** Salvaged-module condition, or null for a factory module. */
  readonly condition: string | null
}

export interface LockerRow {
  readonly key: string
  readonly module: string
  readonly mk: string
  readonly count: string
  /** Salvaged: its condition; null for a factory module. */
  readonly condition: string | null
}

export interface LoadoutModel {
  readonly slots: readonly LoadoutSlotRow[]
  readonly power: string
  readonly over: boolean
  readonly locker: readonly LockerRow[]
}

export function loadoutModel(data: M2Data, career: Career): LoadoutModel {
  const t = SHIP_TAB.loadout
  const fit = fitSummary(data, career, UI_CONFIG)
  const salvaged = career.ship.salvaged
  const slots = fit.slots.map((s): LoadoutSlotRow => {
    const sv = salvaged?.fitted[s.slot]
    return { slot: s.slot, label: s.label, module: s.moduleName, mk: `Mk ${s.mk}`, power: s.powerLabel, condition: sv === undefined ? null : formatPct(sv.condition) }
  })
  const counts = new Map<ModuleId, number>()
  for (const id of career.ship.locker) counts.set(id, (counts.get(id) ?? 0) + 1)
  const locker: LockerRow[] = []
  for (const [id, n] of counts) {
    const def = resolveModule(data, id)
    locker.push({ key: id, module: def.name, mk: `Mk ${def.mk}`, count: fill(t.count, { n: String(n) }), condition: null })
  }
  for (const s of salvaged?.locker ?? []) {
    const def = resolveModule(data, s.moduleId)
    locker.push({ key: s.sourceId, module: `${def.name} (${t.salvaged})`, mk: `Mk ${s.mk}`, count: fill(t.count, { n: '1' }), condition: formatPct(s.condition) })
  }
  return {
    slots,
    power: fill(t.power, { draw: String(fit.power.draw), budget: String(fit.power.budget) }),
    over: fit.power.over,
    locker,
  }
}

// ---- Cargo ---------------------------------------------------------------------------------------------------------

export type CargoRowKind = 'ware' | 'good' | 'component'

export interface CargoRow {
  readonly key: string
  readonly kind: CargoRowKind
  readonly name: string
  readonly amount: string
  readonly mk: string | null
  readonly condition: string | null
  readonly flags: readonly string[]
  /** "500 cr", or "not for sale" (sealed or story). */
  readonly value: string
  /** Valued at the base price (no logged sell price). */
  readonly base: boolean
}

export interface CargoModel {
  readonly rows: readonly CargoRow[]
  readonly empty: boolean
  readonly capacity: string
  /** Used / capacity, 0..1. */
  readonly fill: number
  /** Credits: the hold at the best last-seen sell prices (base where none), sealed and story cargo excluded. */
  readonly total: number
  readonly totalText: string
  /** The age of the oldest logged price used, or the no-prices line. */
  readonly priceAge: string
}

interface UnitPrice {
  readonly unit: number
  /** Career game seconds of the snapshot, or null = base price. */
  readonly t: number | null
}

/** The best logged sell unit of a strategic ware across every system the pilot logged; base price when none. */
function bestSell(career: Career, id: string, basePrice: number): UnitPrice {
  const i = wareIndexOf(id)
  let best: UnitPrice | null = null
  if (i >= 0 && career.priceLog !== undefined) {
    for (const entry of Object.values(career.priceLog)) {
      const unit = entry.s[i]
      if (unit === undefined || unit < 0) continue
      if (best === null || unit > best.unit) best = { unit, t: entry.t }
    }
  }
  return best ?? { unit: basePrice, t: null }
}

function componentFlags(c: CargoComponent): string[] {
  const f = SHIP_TAB.cargo.flags
  const out: string[] = []
  if (c.contraband === true) out.push(f.contraband)
  if (c.sealed === true) out.push(f.sealed)
  if (c.story === true) out.push(f.story)
  if (c.contractId !== undefined) out.push(f.contract)
  return out
}

export function cargoModel(data: M2Data, career: Career): CargoModel {
  const t = SHIP_TAB.cargo
  const rows: CargoRow[] = []
  let total = 0
  let oldest: number | null = null
  const price = (p: UnitPrice): void => {
    if (p.t !== null && (oldest === null || p.t < oldest)) oldest = p.t
  }
  for (const w of WARE_IDS) {
    const q = career.cargo.wares[w]
    if (!(q > EPS)) continue
    const def = data.wares.wares[WARE_IDS.indexOf(w)]
    const p = bestSell(career, w, def.basePrice)
    price(p)
    const v = q * p.unit
    total += v
    rows.push({ key: w, kind: 'ware', name: def.name, amount: fill(t.tonnes, { t: formatTonnes(q) }), mk: null, condition: null, flags: [], value: formatCr(v), base: p.t === null })
  }
  for (const g of GOOD_IDS) {
    const q = career.cargo.parts?.[g as GoodId] ?? 0
    if (!(q > EPS)) continue
    const p = bestSell(career, g, GOOD_BASE_PRICE[g])
    price(p)
    const v = q * p.unit
    total += v
    const n = Number.isInteger(q) ? String(q) : q.toFixed(1)
    rows.push({ key: g, kind: 'good', name: GOOD_NAMES[g], amount: fill(t.units, { n }), mk: null, condition: null, flags: [], value: formatCr(v), base: p.t === null })
  }
  for (const c of career.cargo.components) {
    const locked = c.sealed === true || c.story === true
    const v = c.value + (c.contents !== undefined ? contentsValue(c.contents, data) : 0)
    if (!locked) total += v
    rows.push({
      key: c.id,
      kind: 'component',
      name: fabItemName(data, c.type),
      amount: fill(t.tonnes, { t: formatTonnes(c.massT) }),
      mk: c.mk === undefined ? null : `Mk ${c.mk}`,
      condition: formatPct(c.condition),
      flags: componentFlags(c),
      value: locked ? t.notForSale : formatCr(v),
      base: false,
    })
  }
  const used = cargoUsedT(career)
  const cap = career.cargo.capacityT
  const seen: number | null = oldest
  return {
    rows,
    empty: rows.length === 0,
    // M10 P5a6b: the hull's class note ("bulk ×1.5") after the hold; unchanged on every shipped hull.
    capacity: holdReadout(fill(t.capacity, { used: formatTonnes(used), cap: formatTonnes(cap).replace(/\.0$/, '') }), career.ship.defId),
    fill: cap > 0 ? clamp01(used / cap) : 0,
    total: Math.round(total),
    totalText: fill(t.value, { value: formatCr(total) }),
    priceAge: seen === null ? t.pricesNever : fill(t.pricesSeen, { age: formatDuration(Math.max(0, career.gameTime - seen)) }),
  }
}

// ---- Jettison ------------------------------------------------------------------------------------------------------

export interface JettisonShareButton {
  readonly share: number
  readonly label: string
}

export interface JettisonModel {
  readonly enabled: boolean
  /** Why it is off (no tug offer open), or null. */
  readonly reason: string | null
  readonly shares: readonly JettisonShareButton[]
}

/** The Jettison buttons: one per `jettison.json shares`, live only while the HUD verb's offer is open. */
export function jettisonModel(phase: JettisonPhase, shares: readonly number[], allLabel: string): JettisonModel {
  const on = phase !== 'none'
  return {
    enabled: on,
    reason: on ? null : SHIP_TAB.cargo.jettisonOff,
    shares: shares.map((share) => ({ share, label: share >= 1 ? allLabel : `${Math.round(share * 100)}%` })),
  }
}

// ---- Fabrication ---------------------------------------------------------------------------------------------------

export interface FabOrderRow {
  readonly id: string
  readonly module: string
  readonly progress: string
  /** Delivered / required tonnes (the carry counts once it is held), 0..1. */
  readonly fraction: number
  readonly carry: string | null
  readonly ready: boolean
  readonly collect: string
  /** The Career station id to collect at. */
  readonly stationId: StationId
}

export interface FabModel {
  readonly orders: readonly FabOrderRow[]
}

function sumT(m: Partial<Record<GoodOrWare, number>>): number {
  let t = 0
  for (const v of Object.values(m)) t += v ?? 0
  return t
}

function carryAmount(id: string, v: number): string {
  return wareIndexOf(id) >= 0 || (GOOD_IDS as readonly string[]).includes(id) ? `${formatTonnes(v)} t` : String(Math.round(v))
}

export function fabModel(data: M2Data, career: Career, stationName: (id: StationId) => string): FabModel {
  const t = SHIP_TAB.fabrication
  const orders = (career.fabOrders ?? []).map((o): FabOrderRow => {
    const need = sumT(o.required)
    let done = 0
    for (const [id, v] of Object.entries(o.delivered)) done += Math.min(v ?? 0, o.required[id as GoodOrWare] ?? 0)
    const carryDone = o.carry.need <= 0 || o.carry.have >= o.carry.need - EPS
    const ready = done >= need - EPS && carryDone
    return {
      id: o.id,
      module: resolveModule(data, o.moduleId).name,
      progress: fill(t.progress, { done: formatTonnes(done), need: formatTonnes(need) }),
      fraction: need > 0 ? clamp01(done / need) : 1,
      carry:
        o.carry.need > 0
          ? fill(t.carry, { name: fabItemName(data, o.carry.id), have: carryAmount(o.carry.id, o.carry.have), need: carryAmount(o.carry.id, o.carry.need) })
          : null,
      ready,
      collect: fill(t.collectAt, { station: stationName(o.stationId) }),
      stationId: o.stationId,
    }
  })
  return { orders }
}

